
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useTranslation } from "@/hooks/useTranslation";
import { useCsrf } from "@/hooks/use-csrf";
import DOMPurify from 'dompurify';

interface PasswordData {
  password: string;
  confirmPassword: string;
}

export function usePasswordUpdate() {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [passwordData, setPasswordData] = useState<PasswordData>({
    password: "",
    confirmPassword: ""
  });
  const { csrfToken, refreshCsrfToken } = useCsrf();

  const updatePasswordData = (field: keyof PasswordData, value: string) => {
    // Sanitize the input
    const sanitizedValue = DOMPurify.sanitize(value);
    setPasswordData(prev => ({ ...prev, [field]: sanitizedValue }));
  };

  const resetPasswordData = () => {
    setPasswordData({
      password: "",
      confirmPassword: ""
    });
  };

  const validatePassword = (): boolean => {
    // Validate password length
    if (passwordData.password.length < 8) {
      toast.error("New password must be at least 8 characters long");
      return false;
    }

    if (passwordData.password.length > 25) {
      toast.error("New password must not exceed 25 characters");
      return false;
    }

    // Validate password against common patterns
    const dangerousPatterns = [
      /<script/i, 
      /javascript:/i,
      /on\w+=/i
    ];
    
    if (dangerousPatterns.some(pattern => pattern.test(passwordData.password))) {
      toast.error("Password contains potentially unsafe content");
      return false;
    }

    // Check that passwords match
    if (passwordData.password !== passwordData.confirmPassword) {
      toast.error("Passwords do not match");
      return false;
    }

    return true;
  };

  const updatePassword = async (): Promise<boolean> => {
    if (!validatePassword()) {
      return false;
    }

    setIsLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: passwordData.password,
        data: {
          password_updated_at: new Date().toISOString(),
          csrfToken: csrfToken
        }
      });
      
      if (error) throw error;
      
      // Get a fresh CSRF token after password change
      refreshCsrfToken();
      
      toast.success("Password updated successfully");
      resetPasswordData();
      return true;
    } catch (error: any) {
      toast.error(error.message);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    passwordData,
    updatePasswordData,
    resetPasswordData,
    isLoading,
    updatePassword
  };
}
